import 'react-day-picker/dist/style.css';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from 'react';
import { DEFAULT_MIN_DATE } from '@/shared/config';
import { useDateRange } from '@/shared/lib';
import { Button } from '@/shared/components/ui';
import { ModalComponentWrapper } from '@/shared/components/ModalComponentWrapper';
import { Calendar } from '@/shared/components/ui/calendar';
import { PickerNavButton } from '@/shared/components/PickerNavButton';
import { MonthSelect } from './MonthSelect';
import { YearSelect } from './YearSelect';

type Props = {
  onApply: (date?: Date) => void;
  initDate?: Date;
};

export function BirthdayDateModal({ onApply, initDate }: Props) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const maxDate = useMemo(() => new Date(), []);

  const {
    locale,
    limits: { minDate },
    date: { selectedDate, setSelectedDate },
    calendar: { currentMonth, setCurrentMonth },
    view: { handleMonthChange },
    canNavigate,
  } = useDateRange({ minDate: DEFAULT_MIN_DATE, maxDate, initDate });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const daySize = Math.floor(el.offsetWidth / 7);
    el.style.setProperty('--day-size', `${daySize}px`);
  }, []);

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex, 1));
  };

  const handleYearSelect = (year: number) => {
    const candidate = new Date(year, currentMonth.getMonth(), 1);
    if (candidate > maxDate) {
      setCurrentMonth(new Date(year, maxDate.getMonth(), 1));
    } else if (candidate < minDate) {
      setCurrentMonth(new Date(year, minDate.getMonth(), 1));
    } else {
      setCurrentMonth(candidate);
    }
  };

  const applyText = selectedDate
    ? t('dateRangeModal.birthdayApplyButton', {
        date: (() => {
          const day = format(selectedDate, 'dd');
          const month = format(selectedDate, 'MMMM', { locale });
          const year = format(selectedDate, 'yyyy');
          return `${day} ${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
        })(),
      })
    : t('dateRangeModal.birthdayPlaceholder');

  const handleApply = () => {
    if (!selectedDate) return;
    onApply(selectedDate);
  };

  return (
    <ModalComponentWrapper
      title="dateRangeModal.birthdayTitle"
      className="rounded-[3.2rem]"
      contentClassName="gap-[2.4rem] sm:gap-[2.4rem]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[0.8rem]">
          <MonthSelect
            currentMonth={currentMonth}
            locale={locale}
            minDate={minDate}
            maxDate={maxDate}
            onChange={handleMonthSelect}
          />
          <YearSelect
            currentMonth={currentMonth}
            minDate={minDate}
            maxDate={maxDate}
            onChange={handleYearSelect}
          />
        </div>
        <div className="flex items-center">
          <PickerNavButton
            direction="prev"
            onClick={() => handleMonthChange('prev')}
            isDisabled={!canNavigate.prev}
          />
          <PickerNavButton
            direction="next"
            onClick={() => handleMonthChange('next')}
            isDisabled={!canNavigate.next}
          />
        </div>
      </div>

      <div ref={containerRef}>
        <Calendar
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          locale={locale}
          selected={selectedDate}
          onSelect={setSelectedDate}
          disabled={(date: Date) => date < minDate || date > maxDate}
        />
      </div>

      <Button
        size="lg"
        disabled={!selectedDate}
        onClick={handleApply}
        className="disabled:bg-muted disabled:text-secondary disabled:opacity-100">
        {applyText}
      </Button>
    </ModalComponentWrapper>
  );
}
